// Shared config for the Share-as-Signal feature.

// Canonical base for shortlinks / QR / OG (env-overridable; defaults to the live domain).
// Safety net: NEXT_PUBLIC_SITE_URL is currently the rocket staging domain in prod, which would
// send every shared preview / QR / shortlink to the wrong host. Until that env is fixed we refuse
// the staging domain and fall back to the canonical site. (Fix the env to silence this.)
const CANONICAL_BASE = 'https://www.showmetrade.com';
function resolveShareBase(): string {
  const raw = (process.env.NEXT_PUBLIC_SITE_URL || '').replace(/\/+$/, '');
  if (!raw || /builtwithrocket\.new/i.test(raw) || /\.vercel\.app$/i.test(raw)) return CANONICAL_BASE;
  return raw;
}
export const SHARE_BASE = resolveShareBase();

export const SIGNAL_BUCKET = 'signal-assets';

// 30-day attribution cookie set by /i/<code> and consumed at signup.
export const SIGNAL_REF_COOKIE = 'smt_signal_ref';

// Mandatory, non-removable on every asset, caption and OG description (§8 regulatory).
export const DISCLAIMER = 'Educational · simulated · not financial advice';

// Per-user daily auto-broadcast cap (§5). Share-only/device actions are NOT limited.
export const DAILY_AUTOPOST_LIMIT = Number(process.env.SMT_DAILY_AUTOPOST_LIMIT || 10);

// Brand palette (kept in sync with the app's theme — design_handoff_showmetrade, warm-light).
export const BRAND = {
  bg: '#f6f4ee',
  panel: '#ffffff',
  border: '#e3dfd3',
  text: '#1c2b26',
  sub: '#63736b',
  faint: '#8c9891',
  accent: '#0c5b41',
  long: '#0e8a4d',
  short: '#d93843',
  gold: '#b07c1f',
};

export function shortlink(code: string, refUsername?: string): string {
  // ref = in-app attribution (resolved by the /i redirector). utm_* = GA4 Acquisition
  // attribution — the /i redirector forwards these to the landing page so GA sees them.
  const params = new URLSearchParams();
  if (refUsername) params.set('ref', refUsername);
  params.set('utm_source', 'showmetrade');
  params.set('utm_medium', 'referral');
  params.set('utm_campaign', 'signal_share');
  return `${SHARE_BASE}/i/${code}?${params.toString()}`;
}

export function ideaUrl(tradeId: string): string {
  return `${SHARE_BASE}/ideas/${tradeId}`;
}

// URL-safe shortcode (no ambiguous chars). Caller ensures uniqueness against TradeIdea.shareShortcode.
export function makeShortcode(len = 7): string {
  const alphabet = '23456789abcdefghjkmnpqrstuvwxyz';
  let out = '';
  for (let i = 0; i < len; i++) {
    // crypto.getRandomValues is available in Edge/Node 18+ runtimes.
    const r = (globalThis.crypto?.getRandomValues(new Uint32Array(1))[0] ?? Math.floor(Math.random() * 0xffffffff));
    out += alphabet[r % alphabet.length];
  }
  return out;
}

export type SignalCardData = {
  ticker: string;
  direction: 'BUY' | 'SELL' | string;
  market: string;
  timeframe: string;
  status: string;
  entry: number;
  stopLoss: number;
  takeProfit: number;
  rr: string;            // e.g. "2.4" or "—"
  username: string;
  tier?: string | null;
  qrDataUrl?: string | null;
  realizedR?: number | null; // settled WIN → big "+2.3R" result band instead of the planned R:R
};

const TF: Record<string, string> = { M1: '1m', M5: '5m', M15: '15m', M30: '30m', H1: '1H', H4: '4H', D1: 'D1' };
export const tfLabel = (t: string) => TF[t] || t;

const STATUS: Record<string, { label: string; color: string }> = {
  OPEN:          { label: 'OPEN',          color: '#3e5fbf' },
  WIN:           { label: 'HIT TP',        color: '#0e8a4d' },
  LOSS:          { label: 'HIT SL',        color: '#d93843' },
  BE:            { label: 'CLOSED',        color: '#b07c1f' },
  PENDING:       { label: 'PENDING',       color: '#8c9891' },
  NOT_TRIGGERED: { label: 'NOT TRIGGERED', color: '#63736b' },
};
export const statusInfo = (s: string) => STATUS[s] || STATUS.OPEN;

export function fmtPrice(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

export function computeRR(entry: number, sl: number, tp: number, direction: string): string {
  const isBuy = ['BUY', 'LONG'].includes(String(direction).toUpperCase());
  const risk = isBuy ? entry - sl : sl - entry;
  const reward = isBuy ? tp - entry : entry - tp;
  if (!(risk > 0) || !(reward > 0)) return '—';
  return (reward / risk).toFixed(1);
}
